import { useEffect, useState } from "react";
import { Link, useLocation } from "@tanstack/react-router";
import { useAuth } from "@/context/auth-context";
import { useShopPromo } from "@/hooks/useShopPromo";
import { activateShopBanner } from "@/hooks/useShopBanner";
import ShopPromoModal from "@/components/application/modals/ShopPromoModal";

import goodScoreBoy from "@/assets/imgs/good-score-boy.png";
import goodScoreGirl from "@/assets/imgs/good-score-girl.png";
import lowScoreBoy from "@/assets/imgs/low-score-boy.png";
import lowScoreGirl from "@/assets/imgs/low-score-girl.png";
import greatScoreBoy from "@/assets/imgs/great-score-boy.png";
import greatScoreGirl from "@/assets/imgs/great-score-girl.png";
import bgImg from "@/assets/imgs/bg-quiz-result.svg";

interface FeedbackProps {
  score: number;
  starsToFill: number;
  slug?: string;
  totalQuestions?: number;
  summaryLevel?: string;
}

const Feedback = ({ score, slug, totalQuestions = 10, summaryLevel }: FeedbackProps) => {
  const { user } = useAuth();
  const location = useLocation();
  const { shouldShowPromo, dismissPromo } = useShopPromo();

  const [sentence, setSentence] = useState("");
  const [imgSrc, setImgSrc] = useState("");

  // Check if we're on a book quiz results page
  const isBookResults = location.pathname.includes('/books/quiz-results/');

  const isBoy = false;

  // Activate the shop banner when the promo modal is shown
  useEffect(() => {
    if (shouldShowPromo) {
      activateShopBanner();
    }
  }, [shouldShowPromo]);

  useEffect(() => {
    const percentage = (score / totalQuestions) * 100;

    switch (true) {
      case percentage <= 50:
        setSentence("You can definitely do better, keep it up, " + user?.name);
        isBoy ? setImgSrc(lowScoreBoy) : setImgSrc(lowScoreGirl);
        break;

      case percentage > 50 && percentage < 80:
        setSentence(
          "Nice work! A little more effort and you'll ace it, " + user?.name
        );
        isBoy ? setImgSrc(goodScoreBoy) : setImgSrc(goodScoreGirl);
        break;

      case percentage >= 80:
        setSentence("Fantastic! You're a star, " + user?.name + "!");
        isBoy ? setImgSrc(greatScoreBoy) : setImgSrc(greatScoreGirl);
        break;

      default:
        break;
    }
  }, [score, totalQuestions, user?.name]);
  return (
    <section
      className="h-screen quiz-result-page"
      style={{ backgroundImage: `url(${bgImg})` }}
    >
      <div className=" container mx-auto px-2 lg:px-0 py-16 flex flex-col items-center justify-center text-center gap-y-4 h-full">
        {/* <QuizScore score={score} total={10} /> */}
        <div className="flex items-end">
          <h3
            className="font-[800] color-primary me-6 text-[32px] lg:text-[54px]"
            style={{ lineHeight: 1 }}
          >
            You Scored: {score}/{totalQuestions}
          </h3>
          <img src={imgSrc} alt="" height={70} width={70} />
        </div>
        <h3 className="font-[800] color-primary text-[32px] lg:text-[54px]">
          {sentence}
        </h3>
        <div className="flex flex-col lg:flex-row items-center">
          {slug && (
            <Link
              to={isBookResults ? "/books/search" : "/articles/$slug"}
              params={!isBookResults ? { slug } : undefined}
              search={!isBookResults && summaryLevel ? { summaryLevel } : undefined}
            >
              <button className="orange-button lg:me-5">
                {isBookResults ? "Back to Book Search" : "Go back to the article"}
              </button>
            </Link>
          )}
          <Link
            to="/dashboard"
            className="font-semibold text-[20px] color-primary font-[700] flex items-center quiz-result-page_link"
          >
            <span className="me-2">Back to dashboard</span>
            <span className="text-[32px] quiz_icon">➮</span>
          </Link>
        </div>
      </div>

      {/* Shop Promo Modal */}
      <ShopPromoModal
        isOpen={shouldShowPromo}
        onClose={dismissPromo}
      />
    </section>
  );
};

export default Feedback;
